/**
 * FolderManager — folder organization with drag-drop for gallery files.
 * Replaces window.FolderManager.
 */
import { useState, useEffect, useRef, type FC } from 'react';
import { Icon } from './Icon';
import * as dataService from '../services/dataService';
import type { FileItem } from '../stores/appStore';

interface Props {
  files: FileItem[];
  onFolderChange?: (folders: Record<string, string[]>) => void;
  title?: string;
  galleryType?: string;
  auth: { password: string };
  onAddToCollection?: (f: FileItem) => void;
  onDeleteFile?: (name: string) => void;
  toggleSelect?: (name: string) => void;
  selectedFiles?: Set<string>;
  onRenameFile?: (f: FileItem) => void;
  onLoading?: (loading: boolean) => void;
}

export const FolderManager: FC<Props> = ({
  files = [],
  onFolderChange,
  title = 'Папки',
  galleryType = 'upload',
  auth,
  onAddToCollection,
  onDeleteFile,
  toggleSelect,
  selectedFiles,
  onRenameFile,
  onLoading,
}) => {
  const [folders, setFolders] = useState<Record<string, string[]>>({});
  const [openedFolder, setOpenedFolder] = useState<string | null>(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [dragOverInFolderFile, setDragOverInFolderFile] = useState<string | null>(null);
  const [draggedInFolderFile, setDraggedInFolderFile] = useState<string | null>(null);
  const isInitialLoad = useRef(true);

  // Load folders from server
  useEffect(() => {
    setIsLoading(true);
    onLoading?.(true);
    (async () => {
      try {
        const data = await dataService.loadFolders(galleryType, title);
        const loaded = data || {};
        setFolders(loaded);
        onFolderChange?.(loaded);
      } catch {
        onFolderChange?.({});
      } finally {
        setIsLoading(false);
        onLoading?.(false);
        setTimeout(() => {
          isInitialLoad.current = false;
        }, 100);
      }
    })();
  }, [galleryType, title]);

  // Save folders when changed
  useEffect(() => {
    if (!isLoading && !isInitialLoad.current) {
      dataService.saveFolders(galleryType, title, folders).then(() => {
        onFolderChange?.(folders);
      });
    }
  }, [folders]);

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    const name = newFolderName.trim();
    if (folders[name]) {
      alert('Папка с таким названием уже существует');
      return;
    }
    setFolders((prev) => ({ ...prev, [name]: [] }));
    setNewFolderName('');
    setIsCreatingFolder(false);
  };

  const handleDeleteFolder = (folderName: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirm(`Удалить папку "${folderName}" и все её содержимое?`)) return;
    setFolders((prev) => {
      const n = { ...prev };
      delete n[folderName];
      return n;
    });
    if (openedFolder === folderName) setOpenedFolder(null);
  };

  const handleDropOnFolder = (e: React.DragEvent, folderName: string) => {
    e.preventDefault();
    e.stopPropagation();
    const draggedFileName = e.dataTransfer.getData('fileName');
    if (!draggedFileName) return;

    setFolders((prev) => {
      const n = { ...prev };
      // Remove from current folder
      for (const [fName, list] of Object.entries(n)) {
        if (list.includes(draggedFileName)) n[fName] = list.filter((f) => f !== draggedFileName);
      }
      // Add to target
      if (!n[folderName].includes(draggedFileName))
        n[folderName] = [...n[folderName], draggedFileName];
      return n;
    });
  };

  const handleRemoveFileFromFolder = (folderName: string, fileName: string) => {
    setFolders((prev) => ({
      ...prev,
      [folderName]: prev[folderName].filter((f) => f !== fileName),
    }));
  };

  const handleReorderInFolder = (folderName: string, dragged: string, target: string) => {
    if (!folderName || !dragged || !target || dragged === target) return;
    setFolders((prev) => {
      const list = [...(prev[folderName] || [])];
      const from = list.indexOf(dragged);
      const to = list.indexOf(target);
      if (from === -1 || to === -1) return prev;
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      return { ...prev, [folderName]: list };
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">{title}</h3>
        <button
          onClick={() => setIsCreatingFolder(true)}
          className="p-1.5 rounded-md bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 transition-all"
          title="Создать папку"
        >
          <Icon name="folder-plus" className="w-4 h-4 text-indigo-400" />
        </button>
      </div>

      {isCreatingFolder && (
        <div className="flex gap-2 p-2 bg-slate-900/50 rounded-md">
          <input
            type="text"
            placeholder="Название папки..."
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder();
              else if (e.key === 'Escape') {
                setIsCreatingFolder(false);
                setNewFolderName('');
              }
            }}
            className="bg-transparent border-none outline-none text-white text-sm flex-1 placeholder:text-slate-600"
            autoFocus
          />
          <button
            onClick={handleCreateFolder}
            className="px-2 py-1 rounded-md bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 border border-indigo-500/30 text-xs font-medium"
          >
            ✓
          </button>
          <button
            onClick={() => {
              setIsCreatingFolder(false);
              setNewFolderName('');
            }}
            className="px-2 py-1 rounded-md bg-slate-700/20 hover:bg-slate-700/30 text-slate-400 border border-slate-700/30 text-xs font-medium"
          >
            ✕
          </button>
        </div>
      )}

      <div className="folder-grid">
        {Object.entries(folders).map(([folderName, fileList]) => (
          <div
            key={folderName}
            className="folder-card group"
            onClick={() => setOpenedFolder(folderName)}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => handleDropOnFolder(e, folderName)}
          >
            <Icon
              name="folder"
              className="w-10 h-10 text-indigo-400 mb-2 group-hover:text-indigo-300 transition-colors"
            />
            <span className="text-sm font-medium text-slate-200 text-center truncate w-full px-2">
              {folderName}
            </span>
            <span className="text-xs text-slate-500">{fileList.length} файлов</span>
            <button
              onClick={(e) => handleDeleteFolder(folderName, e)}
              className="absolute top-2 right-2 p-1.5 rounded-md hover:bg-red-500/20 text-red-400 opacity-0 group-hover:opacity-100 transition-all"
              title="Удалить папку"
            >
              <Icon name="trash-2" className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Opened folder modal */}
      {openedFolder && (
        <div className="modal-overlay" onClick={() => setOpenedFolder(null)}>
          <div className="modal-content overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-900/50">
              <div className="flex items-center gap-3">
                <Icon name="folder-open" className="w-6 h-6 text-indigo-400" />
                <h2 className="text-xl font-bold text-white">{openedFolder}</h2>
                <span className="text-sm text-slate-400">
                  ({folders[openedFolder]?.length || 0})
                </span>
              </div>
              <button
                onClick={() => setOpenedFolder(null)}
                className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white"
              >
                <Icon name="x" className="w-6 h-6" />
              </button>
            </div>
            <div className="folder-file-grid p-6 custom-scroll flex-1">
              {(folders[openedFolder] || []).map((fileName) => {
                const f = files.find((x) => x.name === fileName);
                if (!f) return null;
                const isSelected = selectedFiles?.has(f.name);
                return (
                  <div
                    key={f.name}
                    className={`gallery-item group border transition-all ${isSelected ? 'border-indigo-500 ring-1 ring-indigo-500/50' : 'border-white/5'} ${dragOverInFolderFile === f.name ? 'ring-2 ring-indigo-400/60 border-indigo-400' : ''}`}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('fileName', f.name);
                      setDraggedInFolderFile(f.name);
                    }}
                    onDragEnd={() => {
                      setDraggedInFolderFile(null);
                      setDragOverInFolderFile(null);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (draggedInFolderFile && draggedInFolderFile !== f.name)
                        setDragOverInFolderFile(f.name);
                    }}
                    onDragLeave={() => {
                      if (dragOverInFolderFile === f.name) setDragOverInFolderFile(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleReorderInFolder(
                        openedFolder!,
                        e.dataTransfer.getData('fileName') || draggedInFolderFile || '',
                        f.name,
                      );
                      setDraggedInFolderFile(null);
                      setDragOverInFolderFile(null);
                    }}
                  >
                    <img
                      src={f.thumb || f.url}
                      className="gallery-image bg-slate-900"
                      loading="lazy"
                    />
                    <div
                      className="absolute top-2 left-2 z-10 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect?.(f.name);
                      }}
                    >
                      <div
                        className={`w-6 h-6 rounded flex items-center justify-center transition-colors shadow-lg backdrop-blur-sm ${isSelected ? 'bg-indigo-600 text-white' : 'bg-black/40 hover:bg-black/60 text-white/50 border border-white/20'}`}
                      >
                        {isSelected && <Icon name="check" className="w-4 h-4" />}
                      </div>
                    </div>
                    <div className="gallery-overlay">
                      <div className="mb-3">
                        <p className="text-xs font-medium text-white truncate drop-shadow-md">
                          {f.name.replace(/\.[^/.]+$/, '')}
                        </p>
                        {f.product_name && (
                          <p className="text-[10px] text-indigo-200 truncate drop-shadow-md">
                            {f.product_name}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {onRenameFile && (
                          <button
                            onClick={() => onRenameFile(f)}
                            className="flex-1 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white p-2 rounded-lg transition-colors flex items-center justify-center min-w-[32px]"
                            title="Переименовать"
                          >
                            <Icon name="pencil" className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            const link =
                              f.category === 'products' && f.short_url ? f.short_url : f.url;
                            navigator.clipboard.writeText(window.location.origin + link);
                          }}
                          className="flex-1 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white p-2 rounded-lg transition-colors flex items-center justify-center min-w-[32px]"
                          title="Копировать ссылку"
                        >
                          <Icon name="link" className="w-4 h-4" />
                        </button>
                        {onAddToCollection && (
                          <button
                            onClick={() => onAddToCollection(f)}
                            className="flex-1 bg-indigo-500/80 hover:bg-indigo-500 backdrop-blur-md text-white p-2 rounded-lg transition-colors flex items-center justify-center min-w-[32px]"
                            title="Добавить в коллекцию"
                          >
                            <Icon name="plus" className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveFileFromFolder(openedFolder!, fileName)}
                          className="flex-1 bg-amber-500/80 hover:bg-amber-500 backdrop-blur-md text-white p-2 rounded-lg transition-colors flex items-center justify-center min-w-[32px]"
                          title="Убрать из папки"
                        >
                          <Icon name="folder-minus" className="w-4 h-4" />
                        </button>
                        {onDeleteFile && (
                          <button
                            onClick={() => {
                              if (confirm('Удалить файл навсегда?')) onDeleteFile(f.name);
                            }}
                            className="flex-1 bg-red-500/80 hover:bg-red-500 backdrop-blur-md text-white p-2 rounded-lg transition-colors flex items-center justify-center min-w-[32px]"
                            title="Удалить файл навсегда"
                          >
                            <Icon name="trash-2" className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {(folders[openedFolder] || []).length === 0 && (
                <div className="col-span-full h-40 flex flex-col items-center justify-center text-slate-500 border-2 border-dashed border-slate-700/50 rounded-xl">
                  <Icon name="ghost" className="w-8 h-8 opacity-50 mb-2" />
                  <p>Папка пуста</p>
                  <p className="text-xs">Перетащите файлы на папку, чтобы добавить их</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
